import { useRandomId, warnAboutMissingStyles } from '@entur/utils';
import React from 'react';
import classNames from 'classnames';
import { Heading5, SubParagraph, Paragraph, Heading2, Heading3, Heading4 } from '@entur/typography';
import { UnmountClosed } from 'react-collapse';
import { DownArrowIcon } from '@entur/icons';
function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
function _objectDestructuringEmpty(t) {
  if (null == t) throw new TypeError("Cannot destructure " + t);
}
function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (e.includes(n)) continue;
    t[n] = r[n];
  }
  return t;
}
var AccordionContext = /*#__PURE__*/React.createContext(null);
var Accordion = function Accordion(_ref) {
  var rest = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
  var currentlyOpenState = React.useState(null);
  return React.createElement(AccordionContext.Provider, _extends({
    value: currentlyOpenState
  }, rest));
};
var useAccordion = function useAccordion(_ref2) {
  var id = _ref2.id,
    defaultOpen = _ref2.defaultOpen;
  var contextValue = React.useContext(AccordionContext);
  if (!contextValue) {
    throw new Error('You need to wrap your AccordionItem inside an Accordion');
  }
  var openId = contextValue[0],
    setOpenId = contextValue[1];
  React.useEffect(function () {
    if (defaultOpen) {
      setOpenId(id);
    }
  }, [defaultOpen, id, setOpenId]);
  var isOpen = openId === id;
  return {
    isOpen: isOpen,
    toggle: function toggle() {
      return setOpenId(isOpen ? null : id);
    }
  };
};
var _excluded$6 = ["open"];
var BaseExpand = function BaseExpand(_ref) {
  var open = _ref.open,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$6);
  return React.createElement(UnmountClosed, {
    isOpened: open
  }, React.createElement("div", _extends({}, rest)));
};
var _excluded$5 = ["open", "className"];
var ExpandArrow = function ExpandArrow(_ref) {
  var _ref$open = _ref.open,
    open = _ref$open === void 0 ? false : _ref$open,
    className = _ref.className,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$5);
  return React.createElement(DownArrowIcon, _extends({
    className: classNames(className, 'eds-expandable-arrow', {
      'eds-expandable-arrow--open': open
    })
  }, rest, {
    "aria-hidden": "true"
  }));
};
var _excluded$4 = ["title", "children", "className", "id", "open", "onToggle", "contentStyle", "disableAnimation"];
var BaseExpandablePanel = function BaseExpandablePanel(_ref) {
  var title = _ref.title,
    children = _ref.children,
    className = _ref.className,
    id = _ref.id,
    open = _ref.open,
    onToggle = _ref.onToggle,
    contentStyle = _ref.contentStyle,
    disableAnimation = _ref.disableAnimation,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$4);
  return React.createElement("div", {
    className: classNames('eds-expandable-panel', className, {
      'eds-expandable-panel--disable-animation': disableAnimation
    })
  }, React.createElement("button", _extends({
    type: "button",
    className: "eds-expandable-panel__trigger",
    onClick: onToggle,
    "aria-expanded": open,
    "aria-controls": open ? id : undefined
  }, rest), React.createElement("span", {
    className: "eds-expandable-panel__grid"
  }, React.createElement(Heading5, {
    margin: "none",
    as: "span"
  }, title), React.createElement("span", {
    className: "eds-expandable-panel__icon-container"
  }, React.createElement(ExpandArrow, {
    open: open
  })))), React.createElement(BaseExpand, {
    className: "eds-expandable-panel__content",
    id: id,
    open: open,
    style: contentStyle
  }, children));
};
var _excluded$3 = ["defaultOpen", "id", "contentStyle"];
var AccordionItem = function AccordionItem(_ref) {
  var _ref$defaultOpen = _ref.defaultOpen,
    defaultOpen = _ref$defaultOpen === void 0 ? false : _ref$defaultOpen,
    overrideId = _ref.id,
    contentStyle = _ref.contentStyle,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$3);
  var randomId = useRandomId('eds-accordion-item');
  var id = overrideId || randomId;
  var _useAccordion = useAccordion({
      id: id,
      defaultOpen: defaultOpen
    }),
    isOpen = _useAccordion.isOpen,
    toggle = _useAccordion.toggle;
  return React.createElement(BaseExpandablePanel, _extends({}, rest, {
    contentStyle: contentStyle,
    id: id,
    onToggle: toggle,
    open: isOpen
  }));
};
var _excluded$2 = ["defaultOpen", "contentStyle"];
var ExpandablePanel = function ExpandablePanel(_ref) {
  var _ref$defaultOpen = _ref.defaultOpen,
    defaultOpen = _ref$defaultOpen === void 0 ? false : _ref$defaultOpen,
    contentStyle = _ref.contentStyle,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  var randomId = useRandomId('eds-expandable');
  var _React$useState = React.useState(defaultOpen),
    isOpen = _React$useState[0],
    setOpen = _React$useState[1];
  return React.createElement(BaseExpandablePanel, _extends({
    id: randomId,
    open: isOpen,
    onToggle: function onToggle() {
      return setOpen(function (prev) {
        return !prev;
      });
    },
    contentStyle: contentStyle
  }, rest));
};
var _excluded$1 = ["children", "open", "onToggle", "as"];
var ExpandableTextButton = function ExpandableTextButton(_ref) {
  var children = _ref.children,
    open = _ref.open,
    onToggle = _ref.onToggle,
    _ref$as = _ref.as,
    Component = _ref$as === void 0 ? Heading5 : _ref$as,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  return React.createElement("button", _extends({
    className: "eds-expandable-text__trigger",
    "aria-expanded": open,
    type: "button",
    onClick: onToggle
  }, rest), React.createElement(Component, null, children), React.createElement(ExpandArrow, {
    open: open,
    className: "eds-expandable-text__arrow"
  }));
};
var _excluded = ["title", "children", "defaultOpen", "contentStyle", "titleElement", "disableAnimation", "className"];
var ExpandableText = function ExpandableText(_ref) {
  var title = _ref.title,
    children = _ref.children,
    _ref$defaultOpen = _ref.defaultOpen,
    defaultOpen = _ref$defaultOpen === void 0 ? false : _ref$defaultOpen,
    contentStyle = _ref.contentStyle,
    _ref$titleElement = _ref.titleElement,
    titleElement = _ref$titleElement === void 0 ? 'Heading5' : _ref$titleElement,
    disableAnimation = _ref.disableAnimation,
    className = _ref.className,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var randomId = useRandomId('eds-expandable-text');
  var _React$useState = React.useState(defaultOpen),
    isOpen = _React$useState[0],
    setOpen = _React$useState[1];
  var Element = React.useMemo(function () {
    return GetTypographyComponent(titleElement);
  }, [titleElement]);
  return React.createElement("div", {
    className: classNames('eds-expandable-text', className, {
      'eds-expandable-text--disable-animation': disableAnimation
    })
  }, React.createElement(ExpandableTextButton, _extends({
    open: isOpen,
    onToggle: function onToggle() {
      return setOpen(function (prev) {
        return !prev;
      });
    },
    "aria-controls": isOpen ? randomId : undefined,
    as: Element
  }, rest), title), React.createElement(BaseExpand, _extends({
    className: "eds-expandable-text__content",
    id: randomId,
    open: isOpen,
    style: contentStyle
  }, rest), children));
};
function GetTypographyComponent(element) {
  switch (element) {
    case 'Heading5':
      return Heading5;
    case 'Heading4':
      return Heading4;
    case 'Heading3':
      return Heading3;
    case 'Heading2':
      return Heading2;
    case 'Paragraph':
      return Paragraph;
    case 'SubParagraph':
      return SubParagraph;
    default:
      return Heading5;
  }
}
warnAboutMissingStyles('expand', 'typography', 'icons');
export { Accordion, AccordionItem, BaseExpand, ExpandArrow, ExpandablePanel, ExpandableText, ExpandableTextButton, useAccordion };